/** Web Audio constants and node helpers shared by both sonification engines. */

/** Time constant for every parameter glide, seconds. */
export const TIME_CONSTANT_S = 0.08;

/**
 * Uniform output boost applied at a master bus. Multiplies the entire mix
 * without touching the user-facing faders.
 */
export const MASTER_OUTPUT_BOOST = 1.5;

/**
 * Create an AudioContext that is already running. Must be called from inside
 * a user gesture: iOS only honours resume() and the first playback while still
 * inside the gesture, so both happen synchronously before the first await. A
 * one-sample silent buffer is the conventional unlock.
 */
export async function createUnlockedAudioContext(): Promise<AudioContext> {
  // iOS routes Web Audio as "ambient" by default, so the ring/silent switch
  // mutes it. "playback" makes it behave like media (Safari 16.4+).
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  if (nav.audioSession) nav.audioSession.type = "playback";
  const ctx = new AudioContext();
  const resumed = ctx.resume();
  const unlock = ctx.createBufferSource();
  unlock.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
  unlock.connect(ctx.destination);
  unlock.start();
  await resumed;
  return ctx;
}

/** Stop an oscillator, tolerating one that has already stopped or never started. */
export function stopOscillator(osc: OscillatorNode, at?: number): void {
  try {
    osc.stop(at);
  } catch {
    // already stopped
  }
}
